import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Cylinder } from 'lucide-react';
import { CollapsibleCard } from '@/components/admin/CollapsibleCard';
import { adminFetch } from '@/hooks/admin/useAdminApi';

interface Enrollment { enrolled: boolean; rate_overrides: Record<string, number>; email_to: string[]; email_enabled: boolean; notes: string | null; enrolled_at: string | null; unenrolled_at: string | null }

/**
 * Is this account billed a monthly CO₂ tank rental? One switch, backed by
 * orders.tank_rental_enrollments through admin-tank-rentals — the same row
 * the audit portal and Refractor flip. Rates and emails are set on the audit
 * portal's rentals page, linked from here.
 */
export default function TankRentalCard({ qboCustomerId }: { qboCustomerId: string | number | null }) {
  const qc = useQueryClient();
  const id = qboCustomerId == null ? null : String(qboCustomerId);
  const { data, isLoading } = useQuery<{ enrollment: Enrollment | null }>({
    queryKey: ['tank-rental-enrollment', id],
    queryFn: () => adminFetch('admin-tank-rentals', { method: 'POST', body: JSON.stringify({ action: 'get_enrollment', qbo_customer_id: id }) }),
    enabled: !!id,
  });
  const set = useMutation({
    mutationFn: (enrolled: boolean) => adminFetch('admin-tank-rentals', { method: 'POST', body: JSON.stringify({ action: 'set_enrollment', qbo_customer_id: id, enrolled, source: 'brix_order_admin' }) }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['tank-rental-enrollment', id] }),
  });
  const [err, setErr] = useState<string | null>(null);
  const enrolled = data?.enrollment?.enrolled === true;
  if (!id) return null;

  return (
    <CollapsibleCard
      title="Gas cylinder rental fees"
      icon={<Cylinder className="h-4 w-4" />}
      summary={isLoading ? '…' : enrolled ? 'On · billed monthly' : 'Off — cylinders not billed'}
      defaultOpen={enrolled}
      storageKey="tank-rental"
      tone={enrolled ? 'active' : 'default'}
      action={<a className="text-xs text-brix-primary underline" href={`/audit/rentals/${id}`} target="_blank" rel="noreferrer">Rates &amp; emails →</a>}
    >
      <p className="text-sm text-brix-muted">
        On the program, this customer gets a BTRF-… rental invoice on the 2nd of each month for the cylinders on their account (their last rental invoice plus deliveries, minus pickups). Off, they can still take cylinders — they are just not billed for holding them.
      </p>
      <label className="mt-3 flex items-center gap-3 text-sm text-brix-ink">
        <input type="checkbox" className="h-4 w-4" checked={enrolled} disabled={isLoading || set.isPending} onChange={(e) => { setErr(null); set.mutateAsync(e.target.checked).catch((x) => setErr(x instanceof Error ? x.message : String(x))); }} />
        Bill this customer a monthly gas cylinder rental fee
      </label>
      {data?.enrollment?.enrolled && data.enrollment.enrolled_at && <p className="mt-1 text-xs text-brix-muted">On since {data.enrollment.enrolled_at.slice(0, 10)}{Object.keys(data.enrollment.rate_overrides ?? {}).length ? ' · custom rates set' : ''}{data.enrollment.email_to?.length ? ` · invoices to ${data.enrollment.email_to.join(', ')}` : ''}</p>}
      {err && <p className="mt-2 text-xs text-red-700">{err}</p>}
    </CollapsibleCard>
  );
}
